import { useEffect, useState } from 'react';

/**
 * DEV-ONLY text-scale simulator.
 *
 * Drives `document.documentElement.style.fontSize` (the root font-size that
 * every `rem` — and therefore every Tailwind `text-*` class — resolves
 * against). Because the app's type scale is entirely rem-based, nudging this
 * one value scales the whole UI proportionally, which is exactly what an OS
 * "large text" / accessibility font setting does. Use it to hunt for layouts
 * that overflow, clip, or break at large text sizes.
 *
 * NOTE: this is a throwaway debugging aid, not a product feature. It's mounted
 * only when `import.meta.env.DEV` is true or the build MODE is 'stg' (so it can
 * also be poked on a real phone via the staging app, where the OS font scale
 * stacks on top of this override). Delete the component + its mount in App.tsx
 * when the responsive-text work is done.
 */

// 150% is the product's text-scaling ceiling (see scaling-cap decision). The
// top preset doubles as the "worst case we support" — design/test to here.
const PRESETS = [85, 100, 115, 130, 150] as const;
// Snake-cased per the docs/cold-start-and-localStorage-cache.md naming policy
// (manual localStorage = `hp_*`; Zustand persist = `hp-*`). This is a dev
// tool, so the one-time loss of saved scale on rename is trivial.
const STORAGE_KEY = 'hp_dev_text_scale';
const BASE_PX = 16; // browser default root font-size

function applyScale(percent: number) {
  // Setting font-size in px on <html> sets the rem base directly; inline style
  // wins over any stylesheet rule (Ionic/Tailwind) without !important.
  document.documentElement.style.fontSize = `${(percent / 100) * BASE_PX}px`;
}

export function TextScaleDevTool() {
  const [percent, setPercent] = useState<number>(() => {
    const saved = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0 ? saved : 100;
  });
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    applyScale(percent);
    localStorage.setItem(STORAGE_KEY, String(percent));
  }, [percent]);

  // Clean up the root override if the tool ever unmounts.
  useEffect(() => () => {
    document.documentElement.style.removeProperty('font-size');
  }, []);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="fixed bottom-4 right-4 z-[99999] flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-card-foreground shadow-lg"
        aria-label="Open text-scale dev tool"
      >
        <span className="font-heading text-sm font-bold leading-none">
          {percent}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-[99999] w-60 rounded-xl border border-border bg-card p-3 text-card-foreground shadow-2xl">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-heading text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Text scale · {percent}%
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="rounded px-1.5 text-muted-foreground hover:text-foreground"
          aria-label="Collapse text-scale dev tool"
        >
          ✕
        </button>
      </div>

      <div className="mb-2 grid grid-cols-4 gap-1">
        {PRESETS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPercent(p)}
            className={
              'rounded-md border px-1 py-1.5 text-xs font-semibold transition-colors ' +
              (p === percent
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-accent')
            }
          >
            {p}
          </button>
        ))}
      </div>

      <input
        type="range"
        min={75}
        max={150}
        step={5}
        value={percent}
        onChange={(e) => setPercent(Number(e.target.value))}
        className="w-full accent-primary"
        aria-label="Text scale percentage"
      />
    </div>
  );
}
